import React, { useEffect } from "react";

import { TailwindSelect } from "../../Tailwind";
import { useAppStore } from "../../../shared/stores/useAppStore";
import FieldBindingModal from "../../FieldBindingModal";
import snakeToPascal from "../../../tools";
import { initRelation } from "./Initializer";
import apiService from "../../../services/ApiService";
import dayjs from "dayjs";
import CustomDatePicker from "./reusables/CustomDatePicker";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import FieldError from "../FieldError";
import ErpBindingButton, { ErpBinding } from "./reusables/ErpBindingButton";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";

export default function CustomizableTimingReferenceField({ name, field }) {
    const hasErpConnections = useAppStore(s => s.hasErpConnections);
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache so stale sections are refetched on next
        // open.  Do NOT invalidate SCENARIO_TREE_KEY: its queryFn returns an empty
        // initial state, so an invalidation-triggered refetch would wipe the
        // lazily-loaded tree and cause model-data items to disappear.
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };
    const [mounted, setMounted] = React.useState(false);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();

    const [bindingModalOpen, setBindingModalOpen] = React.useState(false);
    const [bindingKey, setBindingKey] = React.useState<string | null>(null);
    const [bindingRefreshTrigger, setBindingRefreshTrigger] = React.useState(0);
    const [existingBinding, setExistingBinding] = React.useState<ErpBinding | null>(null);

    const [timingChoicesItems, setTimingChoicesItems] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);

    const handleOpenBindingModal = (key: string, binding: ErpBinding | null) => {
        setBindingKey(key);
        setExistingBinding(binding);
        setBindingModalOpen(true);
    };

    const handleBindingCreated = () => {
        setBindingRefreshTrigger(prev => prev + 1);
    };

    const reloadLocalState = () => {
        setMounted(!mounted);
    }

    React.useEffect(() => {
        // Global library snapshot: its ids belong to another database.
        if (field.snapshot) {
            setOptionsLoading(false);
            return;
        }
        setOptionsLoading(true);
        apiService.getTimingOptions(field.id).then(async (response) => {
            if (response && response.ok) {
                const data = await response.json();
                if (Array.isArray(data)) {
                    setTimingChoicesItems(data);

                    // Set timing choice by id if needed
                    if (field.currentValue?.value?.timing_id) {
                        data.forEach((item: any) => {
                            if (item.id == field.currentValue.value.timing_id) {
                                if (field.currentValue.timingChoice == 'null') {
                                    field.currentValue.timingChoice = item;
                                    reloadLocalState();
                                }
                            }
                        });
                    }
                }
            }
            setOptionsLoading(false);
        });
    }, [field.id]);

    const handleChange = (newValueId: string) => {
        // Clear any stale compute error from a previous selection — the new
        // compute run will overwrite it if the field is still invalid.
        field.compute_error = null;

        if (newValueId === 'null' || !newValueId) {
            // Create custom timing
            triggerSave();
            apiService.postModelDataTimingValue(field.id, 'timing').then(async(response) => {
                if (response) {
                    const timing = await response.json()

                    if (field.currentValue) {
                        field.currentValue.value.timing = timing.current_value;

                        field.currentValue.value.timing.periodicity.value = (timing.current_value.periodicity.value) ? timing.current_value.periodicity.value : 'null';

                        field.currentValue.value.timing_id = timing.id;

                        field.currentValue.timingChoice = 'null';
                    }

                    resolveSave();
                    reloadLocalState();
                    invalidateQueries();
                }
            })
        }
        else {
            // Select existing timing
            triggerSave();
            const selectedTiming = timingChoicesItems.find((item: any) => item.id.toString() === newValueId);
            const apiValue = selectedTiming ? selectedTiming.id : null;

            apiService.putModelDataValue(field.id, apiValue).then(
                async(response) => {
                    if (response && selectedTiming) {
                        if (field.currentValue) {
                            field.currentValue.value.timing_id = selectedTiming.id;
                            field.currentValue.timingChoice = selectedTiming;
                            // Reflect the referenced timing's dates immediately so the
                            // read-only Start/End display updates without needing a
                            // leave-and-return refetch (covers the global timing too).
                            field.currentValue.value.timing = {
                                start_date: { value: selectedTiming.start_date ? dayjs(selectedTiming.start_date) : null, address: null },
                                end_date: { value: selectedTiming.end_date ? dayjs(selectedTiming.end_date) : null, address: null },
                                periodicity: { value: selectedTiming.periodicity || 'null', address: null },
                                custom: false,
                            };
                        }
                        resolveSave();
                        reloadLocalState();
                        invalidateQueries();
                    }
                }
            )
        }
    }

    const checkComplete = () => {
        if (!field.currentValue?.value?.timing) {
            field.complete = false;
            field.timingError = null;
            return;
        }

        if (field.currentValue.value.timing.start_date.value && field.currentValue.value.timing.end_date.value) {
            if (field.currentValue.value.timing.start_date.value.diff(field.currentValue.value.timing.end_date.value, 'day') >= 0) {
                field.complete = false;
                field.timingError = 'Start date must be before End date.'
            }
            else if (field.currentValue.value.timing.start_date.value.diff(field.currentValue.value.timing.end_date.value, 'month') == 0) {
                field.complete = false;
                field.timingError = 'Difference Between Dates should be at least one month.'
            }
            else if (field.currentValue.value.timing.periodicity.value !== 'null') {
                field.complete = true;
                field.timingError = null;
            }
            else {
                field.timingError = null;
                field.complete = false;
            }
        }
        else {
            field.complete = false;
            field.timingError = null;
        }
    }

    const handleChangestart_date = (newValue) => {
        if (!field.currentValue?.value?.timing) return;

        const timing = field.currentValue.value.timing;

        triggerSave();
        apiService.putModelDataTimingValue(field.currentValue.value.timing_id,
            newValue.format('YYYY-MM-DD'),
            (timing.end_date.value) ? timing.end_date.value.format('YYYY-MM-DD') : null,
            'monthly',
        ).then(async(response) => {
            if (response) {
                timing.start_date.value = newValue;
                field.start_dateStore.isOpen = false;
                checkComplete();
                resolveSave();
                reloadLocalState();
                invalidateQueries();
            }
        })

    };

    const handleChangeend_date = (newValue) => {
        if (!field.currentValue?.value?.timing) return;

        const timing = field.currentValue.value.timing;

        triggerSave();
        apiService.putModelDataTimingValue(field.currentValue.value.timing_id,
            (timing.start_date.value) ? timing.start_date.value.format('YYYY-MM-DD') : null,
            newValue.format('YYYY-MM-DD'),
            'monthly',
        ).then(async(response) => {
            if (response) {
                timing.end_date.value = newValue;
                field.end_dateStore.isOpen = false;
                checkComplete();
                resolveSave();
                reloadLocalState();
                invalidateQueries();
            }
        })
    };

    const options = [
        { value: 'null', label: 'Custom' },
        ...timingChoicesItems.map((e: any) => ({
            value: e.id.toString(),
            // The scenario-level global timing is a virtual reference (id 'global'):
            // give it a clean label instead of "Scenario | GlobalTiming".
            label: e.id === 'global'
                ? 'Global Timing'
                : `${e.parent_name} | ${snakeToPascal(e.field_name)}`,
        }))
    ];

    // Derive selection from `timing_id` + loaded options rather than the
    // transient `timingChoice` flag: the field's initializer resets
    // `timingChoice = 'null'` on every refetch, which would otherwise force
    // the dropdown back to "Custom" right after picking a reference.
    const matchedTimingItem: any = field.currentValue?.value?.timing_id
        ? timingChoicesItems.find((t: any) => t.id == field.currentValue.value.timing_id)
        : null;
    const isCustomTiming = !matchedTimingItem;
    const currentValue = matchedTimingItem ? matchedTimingItem.id.toString() : 'null';

    return (
        <div>
            <div className="flex flex-row">
                <div>
                    <TailwindSelect
                        value={currentValue}
                        onChange={handleChange}
                        options={options}
                        disabled={isReadOnly || optionsLoading}
                        className="w-[200px]"
                    />
                </div>
                {/* Start/End date are always shown. When a timing reference is
                    selected (`!isCustomTiming`) the dates come from the referenced
                    timing and are displayed read-only; only the custom case is
                    editable (and offers ERP binding). */}
                <div className="ml-4 -mt-5">
                    <div className="mb-3.5">
                        <div className="mb-1 flex items-center gap-1">
                            Start Date
                            {isCustomTiming && hasErpConnections && (
                                <ErpBindingButton
                                    onClick={(binding) => handleOpenBindingModal('start_date', binding)}
                                    fieldId={field.id}
                                    bindingKey="start_date"
                                    refreshTrigger={bindingRefreshTrigger}
                                    currentValue={field.currentValue?.value?.timing?.start_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.timing?.start_date?.value}
                                    appFieldType="datetime"
                                    onSyncApply={(erpValue) => handleChangestart_date(erpValue)}
                                />
                            )}
                        </div>
                        <CustomDatePicker
                            item={field.start_dateStore}
                            currentValue={(field.currentValue?.value?.timing?.start_date && field.currentValue.value.timing.start_date.value) ? field.currentValue.value.timing.start_date.value : null}
                            handleChange={handleChangestart_date}
                            readOnly={!isCustomTiming}
                        />
                    </div>
                    <div className="mb-3.5">
                        <div className="mb-1 flex items-center gap-1">
                            End Date
                            {isCustomTiming && hasErpConnections && (
                                <ErpBindingButton
                                    onClick={(binding) => handleOpenBindingModal('end_date', binding)}
                                    fieldId={field.id}
                                    bindingKey="end_date"
                                    refreshTrigger={bindingRefreshTrigger}
                                    currentValue={field.currentValue?.value?.timing?.end_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.timing?.end_date?.value}
                                    appFieldType="datetime"
                                    onSyncApply={(erpValue) => handleChangeend_date(erpValue)}
                                />
                            )}
                        </div>
                        <CustomDatePicker
                            item={field.end_dateStore}
                            currentValue={(field.currentValue?.value?.timing?.end_date && field.currentValue.value.timing.end_date.value) ? field.currentValue.value.timing.end_date.value : null}
                            handleChange={handleChangeend_date}
                            readOnly={!isCustomTiming}
                        />
                    </div>
                    {
                        (isCustomTiming && field.timingError) ?
                        <div className="text-red-600">
                            {field.timingError}
                        </div> : <div></div>
                    }
                    {isCustomTiming && bindingKey && (
                        <FieldBindingModal
                            open={bindingModalOpen}
                            setOpen={setBindingModalOpen}
                            fieldName={name}
                            field={field}
                            modelDataId={field.model_data_id}
                            modelDataName={name}
                            scenarioId={scenarioId}
                            bindingKey={bindingKey}
                            existingBinding={existingBinding}
                            onBindingCreated={handleBindingCreated}
                            currentValue={
                                bindingKey === 'start_date'
                                    ? field.currentValue?.value?.timing?.start_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.timing?.start_date?.value
                                    : field.currentValue?.value?.timing?.end_date?.value?.format?.('YYYY-MM-DD') || field.currentValue?.value?.timing?.end_date?.value
                            }
                            onSyncApply={(erpValue) => {
                                if (bindingKey === 'start_date') {
                                    handleChangestart_date(erpValue);
                                } else if (bindingKey === 'end_date') {
                                    handleChangeend_date(erpValue);
                                }
                            }}
                        />
                    )}
                </div>
            </div>
            <SaveIndicator status={saveStatus} className="mt-1" />
            <FieldError field={field} />
        </div>
    )
}
